import { StoreState } from '../store';
import { ModelType } from './customization-types';
import { activeFreeTextFilterSet as contourActiveFreeTextFilterSet, deprecatedModelsVisibilitySet as contourDeprecatedModelsVisibilitySet, nonLicensedModelsVisibilitySet as contourNonLicensedModelsVisibilitySet, userHiddenModelsVisibilitySet as contourUserHiddenModelsVisibilitySet, contouringSelectors } from '../contouring/contouringSlice';
import { activeFreeTextFilterSet as doseActiveFreeTextFilterSet, deprecatedModelsVisibilitySet as doseDeprecatedModelsVisibilitySet, nonLicensedModelsVisibilitySet as doseNonLicensedModelsVisibilitySet, userHiddenModelsVisibilitySet as doseUserHiddenModelsVisibilitySet, doseSelectors } from '../dose/doseSlice';
import { activeFreeTextFilterSet as imageActiveFreeTextFilterSet, deprecatedModelsVisibilitySet as imageDeprecatedModelsVisibilitySet, nonLicensedModelsVisibilitySet as imageNonLicensedModelsVisibilitySet, userHiddenModelsVisibilitySet as imageUserHiddenModelsVisibilitySet, imageSelectors } from '../image/imageSlice';
import { activeFreeTextFilterSet as adaptActiveFreeTextFilterSet, deprecatedModelsVisibilitySet as adaptDeprecatedModelsVisibilitySet, nonLicensedModelsVisibilitySet as adaptNonLicensedModelsVisibilitySet, userHiddenModelsVisibilitySet as adaptUserHiddenModelsVisibilitySet, adaptSelectors } from '../adapt/adaptSlice';
import type { PayloadAction } from '@reduxjs/toolkit';

type SupportedModelType = ModelType.Contouring | ModelType.Dose | ModelType.Image | ModelType.Adapt;

export enum ModelFilter {
    Deprecated = 'deprecated',
    NonLicensed = 'non-licensed',
    UserHidden = 'user-hidden',
}

/**
 * Selectors for active free-text filter, mapped by model type.
 */
const activeFreeTextFilterSelectors: Record<SupportedModelType, (state: StoreState) => string> = {
    [ModelType.Contouring]: contouringSelectors.selectActiveFreeTextFilter,
    [ModelType.Dose]: doseSelectors.selectActiveFreeTextFilter,
    [ModelType.Image]: imageSelectors.selectActiveFreeTextFilter,
    [ModelType.Adapt]: adaptSelectors.selectActiveFreeTextFilter,
};

/**
 * Selectors for model visibility state, mapped by model type.
 */
const modelVisibilitySelectors: Record<SupportedModelType, {
    selectAreDeprecatedModelsShown: (state: StoreState) => boolean;
    selectAreNonLicensedModelsShown: (state: StoreState) => boolean;
    selectAreUserHiddenModelsShown: (state: StoreState) => boolean;
}> = {
    [ModelType.Contouring]: {
        selectAreDeprecatedModelsShown: contouringSelectors.selectAreDeprecatedModelsShown,
        selectAreNonLicensedModelsShown: contouringSelectors.selectAreNonLicensedModelsShown,
        selectAreUserHiddenModelsShown: contouringSelectors.selectAreUserHiddenModelsShown,
    },
    [ModelType.Dose]: {
        selectAreDeprecatedModelsShown: doseSelectors.selectAreDeprecatedModelsShown,
        selectAreNonLicensedModelsShown: doseSelectors.selectAreNonLicensedModelsShown,
        selectAreUserHiddenModelsShown: doseSelectors.selectAreUserHiddenModelsShown,
    },
    [ModelType.Image]: {
        selectAreDeprecatedModelsShown: imageSelectors.selectAreDeprecatedModelsShown,
        selectAreNonLicensedModelsShown: imageSelectors.selectAreNonLicensedModelsShown,
        selectAreUserHiddenModelsShown: imageSelectors.selectAreUserHiddenModelsShown,
    },
    [ModelType.Adapt]: {
        selectAreDeprecatedModelsShown: adaptSelectors.selectAreDeprecatedModelsShown,
        selectAreNonLicensedModelsShown: adaptSelectors.selectAreNonLicensedModelsShown,
        selectAreUserHiddenModelsShown: adaptSelectors.selectAreUserHiddenModelsShown,
    },
};

/**
 * Gets the active free-text filter value from the appropriate slice based on model type.
 */
export const getActiveFreeTextFilter = (state: StoreState, modelType: ModelType): string => {
    const selector = activeFreeTextFilterSelectors[modelType as SupportedModelType];
    if (!selector) {
        throw new Error(`Unsupported model type: ${modelType}`);
    }
    return selector(state);
};

/**
 * Gets model visibility state from the appropriate slice based on model type.
 */
export const getVisibilityStates = (state: StoreState, modelType: ModelType) => {
    const selectors = modelVisibilitySelectors[modelType as SupportedModelType];
    if (!selectors) {
        throw new Error(`Unsupported model type: ${modelType}`);
    }
    return {
        areDeprecatedModelsShown: selectors.selectAreDeprecatedModelsShown(state),
        areNonLicensedModelsShown: selectors.selectAreNonLicensedModelsShown(state),
        areUserHiddenModelsShown: selectors.selectAreUserHiddenModelsShown(state),
    };
};

/**
 * Action creators for setting free-text filters, mapped by model type.
 */
const freeTextFilterActions: Record<SupportedModelType, (value: string) => PayloadAction<string>> = {
    [ModelType.Contouring]: contourActiveFreeTextFilterSet,
    [ModelType.Dose]: doseActiveFreeTextFilterSet,
    [ModelType.Image]: imageActiveFreeTextFilterSet,
    [ModelType.Adapt]: adaptActiveFreeTextFilterSet,
};

/**
 * Gets the action creator for setting a free-text filter for the given model type.
 * Throws if model type is not supported.
 */
export const getFreeTextFilterAction = (modelType: ModelType) => {
    const actions = freeTextFilterActions[modelType as SupportedModelType];
    
    if (!actions) {
        throw new Error(`Unsupported model type for free-text filter action: ${modelType}`);
    }

    return actions;
};

/**
 * Action creators for model visibility filters, mapped by model type and filter type.
 */
const modelVisibilityFilterActions = {
    [ModelFilter.Deprecated]: {
        [ModelType.Contouring]: contourDeprecatedModelsVisibilitySet,
        [ModelType.Dose]: doseDeprecatedModelsVisibilitySet,
        [ModelType.Image]: imageDeprecatedModelsVisibilitySet,
        [ModelType.Adapt]: adaptDeprecatedModelsVisibilitySet,
    },
    [ModelFilter.NonLicensed]: {
        [ModelType.Contouring]: contourNonLicensedModelsVisibilitySet,
        [ModelType.Dose]: doseNonLicensedModelsVisibilitySet,
        [ModelType.Image]: imageNonLicensedModelsVisibilitySet,
        [ModelType.Adapt]: adaptNonLicensedModelsVisibilitySet,
    },
    [ModelFilter.UserHidden]: {
        [ModelType.Contouring]: contourUserHiddenModelsVisibilitySet,
        [ModelType.Dose]: doseUserHiddenModelsVisibilitySet,
        [ModelType.Image]: imageUserHiddenModelsVisibilitySet,
        [ModelType.Adapt]: adaptUserHiddenModelsVisibilitySet,
    },
} as const;

/**
 * Gets the action creator for setting model visibility filter for the given model and filter types.
 */
export const getModelVisibilityFilterAction = (
    modelType: ModelType, 
    filterType: ModelFilter.Deprecated | ModelFilter.NonLicensed | ModelFilter.UserHidden
) => {
    return modelVisibilityFilterActions[filterType][modelType as SupportedModelType];
};

